// ===========================================
// PermissionGate Component
// ===========================================
// Conditionally renders children based on whether the current user has
// the required resource + action permission. Renders `fallback` (default: null)
// when the permission check fails.
//
// Usage:
//   <PermissionGate resource="reports" action="export">
//     <ExportButton />
//   </PermissionGate>
//
//   <PermissionGate resource="people_teachers" action="write" fallback={<ReadOnlyView />}>
//     <EditForm />
//   </PermissionGate>

import { ReactNode } from 'react';
import { usePermissions } from '../../hooks/usePermissions';

export interface PermissionGateProps {
  /** The permission resource to check, e.g. "reports" */
  resource: string;
  /** The action to check. Defaults to "read". */
  action?: string;
  /** Content to render when the permission check passes */
  children: ReactNode;
  /** Content to render when the permission check fails. Defaults to null. */
  fallback?: ReactNode;
}

export function PermissionGate({
  resource,
  action = 'read',
  children,
  fallback = null,
}: PermissionGateProps) {
  const { can } = usePermissions();
  return can(resource, action) ? <>{children}</> : <>{fallback}</>;
}
